export const PAGE_SIZE = 105;
export const MIN_SEARCH_LENGTH = 2;
export const SEARCH_DEBOUNCE_MS = 1000;
export const TOOLBAR_REVEAL_MS = 1500;

export const SORT_OPTIONS = [
  { id: "date-added", label: "Date Added" },
  { id: "year", label: "Year" },
  { id: "name", label: "Name (A-Z)" },
  { id: "random", label: "Random" },
  { id: "duration", label: "Duration" },
  { id: "release-date", label: "Release Date" },
  { id: "critic-rating", label: "Critic Rating" },
  { id: "audience-rating", label: "Audience Rating" },
];

export const DEFAULT_SORT = { option: "date-added", direction: "desc" };

export const ALL_CATEGORY = { id: "", name: "All Categories" };
export const ALL_GENRE = { id: "", name: "All Genres" };

const MOVIES_BROWSE = {
  kind: "movies",
  title: "Movies",
  endpoint: "/v2/movies",
  mediaType: "movie",
  showLibrary: true,
  emptyMessage: "No movies found",
  detailPath: (id) => `/web-player/movie/${id}`,
};

const SERIES_BROWSE = {
  kind: "series",
  title: "Series",
  endpoint: "/v2/series",
  mediaType: "tv",
  showLibrary: false,
  emptyMessage: "No series found",
  detailPath: (id) => `/web-player/series/${id}`,
};

export function browseConfig(kind) {
  return kind === "series" ? SERIES_BROWSE : MOVIES_BROWSE;
}

export function createBrowseState() {
  return {
    status: "loading",
    media: [],
    popular: [],
    categories: [],
    genres: [],
    totalItems: 0,
    page: 1,
    hasMore: true,
    category: ALL_CATEGORY,
    genre: ALL_GENRE,
    sort: DEFAULT_SORT,
    search: { active: false, query: "", pending: false },
    error: "",
  };
}

export function nextSort(current, optionId) {
  if (current.option === optionId) {
    return {
      option: optionId,
      direction: current.direction === "desc" ? "asc" : "desc",
    };
  }

  return { option: optionId, direction: "desc" };
}

function isAllCategory(category) {
  return !category?.id;
}

function isAllGenre(genre) {
  return !genre?.name || genre.name === ALL_GENRE.name;
}

export function mediaQuery(state, page) {
  const query = {
    page: String(page),
    limit: String(PAGE_SIZE),
    sortBy: `${state.sort.option}-${state.sort.direction}`,
  };

  if (state.search.active && state.search.query) {
    query.term = state.search.query;
  }

  if (state.category?.id) {
    query.categoryIds = state.category.id;
  }

  if (!isAllGenre(state.genre)) {
    query.genres = state.genre.name;
  }

  return query;
}

export function showPopular(state) {
  return (
    state.popular.length > 0 &&
    isAllCategory(state.category) &&
    isAllGenre(state.genre) &&
    state.sort.option === DEFAULT_SORT.option &&
    state.sort.direction === DEFAULT_SORT.direction &&
    !state.search.active
  );
}

export function showGenrePill(genres) {
  return genres.length > 0;
}

export function showLibraryPills(isAuthenticated) {
  return isAuthenticated;
}

export function isCategoryLocked(category, locks, unlockedIds) {
  if (!category?.id) return false;
  if (!locks || locks[category.id] == null) return false;

  return !unlockedIds.includes(String(category.id));
}

export function pinMatches(category, pin, locks) {
  return locks?.[category.id] === pin;
}

function readName(item) {
  return String(item.categoryName || item.name || "").trim();
}

export function normalizeCategories(data) {
  if (!Array.isArray(data)) return [];

  return data
    .map((item) => ({
      id: String(item.categoryId ?? item.id ?? ""),
      name: readName(item),
      locked: Boolean(item.locked || item.isLocked),
    }))
    .filter((item) => item.name && item.name.toLowerCase() !== "all");
}

export function mapMediaItem(item, mediaType = "movie") {
  const id = item.mediaId && item.mediaId !== -1 ? item.mediaId : item.id;

  return {
    id,
    mediaId: id,
    title: item.name || item.title,
    name: item.name || item.title,
    poster: item.posterPath || item.poster,
    posterPath: item.posterPath || item.poster,
    backdropPath: item.backdropPath,
    year: item.year,
    rating: item.rating,
    genres: item.genres,
    description: item.description,
    rottenTomatoes: item.rottenTomatoes,
    tmdbId: item.tmdbId,
    isBookmarked: Boolean(item.isBookmarked),
    dateAdded: item.dateAdded,
    duration: item.duration,
    mediaType: item.mediaType || mediaType,
  };
}

function clearedPage(state) {
  return {
    ...state,
    media: [],
    popular: [],
    page: 1,
    hasMore: true,
    status: "content",
    error: "",
  };
}

export function reduceBrowse(state, action) {
  switch (action.type) {
    case "selectCategory": {
      if (isAllCategory(action.category)) {
        return clearedPage({
          ...state,
          category: ALL_CATEGORY,
          genre: ALL_GENRE,
          sort: DEFAULT_SORT,
          search: { active: false, query: "", pending: false },
        });
      }

      return clearedPage({
        ...state,
        category: action.category,
        genre: ALL_GENRE,
      });
    }
    case "selectGenre":
      return clearedPage({
        ...state,
        genre: isAllGenre(action.genre) ? ALL_GENRE : action.genre,
      });
    case "applySort":
      return clearedPage({
        ...state,
        sort: action.sort,
      });
    case "submitSearch": {
      const query = action.query.trim();

      if (query.length < MIN_SEARCH_LENGTH) return state;

      return {
        ...state,
        search: { active: true, query, pending: true },
      };
    }
    case "clearSearch":
      return clearedPage({
        ...state,
        category: ALL_CATEGORY,
        genre: ALL_GENRE,
        sort: DEFAULT_SORT,
        search: { active: false, query: "", pending: false },
      });
    case "mediaPage": {
      const media = action.reset
        ? action.media
        : [...state.media, ...action.media];

      return {
        ...state,
        status: "content",
        error: "",
        media,
        popular: action.reset ? action.popular || [] : state.popular,
        totalItems: action.totalItems ?? state.totalItems,
        page: action.page,
        hasMore: action.page < action.totalPages,
        search: { ...state.search, pending: false },
      };
    }
    case "categories":
      return { ...state, categories: action.categories };
    case "genres":
      return { ...state, genres: action.genres };
    case "fail":
      if (state.status === "loading" && state.media.length === 0) {
        return { ...state, status: "error", error: action.message };
      }

      return {
        ...state,
        status: "content",
        search: { ...state.search, pending: false },
        error: action.message,
      };
    default:
      return state;
  }
}
